/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ParentSize } from "@visx/responsive";
import { When } from "react-if";
import styled from "styled-components";
import { FlexContainer, Spacer, theme } from "@nordcloud/gnui";
import { ReservationUtilizationTimePeriod } from "~/generated/graphql";

import { DataContainer, DataItem } from "~/components/Data";
import { useCurrency } from "~/hooks";
import { formatMoney, isNotEmpty, isNotNil } from "~/tools";
import { Legend } from "../components";
import { DataItem as UtilisationDataItem } from "../types";
import {
  MonthlyUtilisationChart,
  monthlyUtilizationChartLegend,
} from "./MonthlyUtilisationChart";

type Props<T> = {
  loading: boolean;
  chartData: T[];
  benefit: string;
  forecast: string;
  utilizationValue: number;
  period?: ReservationUtilizationTimePeriod;
};

export function UtilizationChart<T extends UtilisationDataItem>({
  loading,
  chartData,
  benefit,
  forecast,
  utilizationValue,
  period,
}: Props<T>) {
  const { currency } = useCurrency();

  return (
    <>
      <FlexContainer justifyContent="center">
        <ParentSize>
          {(parent) => (
            <MonthlyUtilisationChart
              width={parent.width}
              height={280}
              data={chartData.filter(isNotNil)}
              loading={loading}
              period={period}
            />
          )}
        </ParentSize>
      </FlexContainer>
      <Spacer height={theme.spacing.spacing03} />
      <FlexContainer justifyContent="flex-end">
        <Legend inline data={monthlyUtilizationChartLegend} />
      </FlexContainer>
      <Spacer height={theme.spacing.spacing04} />
      <When condition={isNotEmpty(chartData)}>
        <Line />
        <DataContainer>
          <DataItem label="Total Utilisation" value={`${utilizationValue}%`} />
          {forecast && (
            <DataItem label="Total Forecast" value={`${forecast}%`} />
          )}
          <DataItem
            label="Total Benefits"
            value={formatMoney(benefit, currency)}
            valueColor={theme.color.text.success}
          />
        </DataContainer>
      </When>
    </>
  );
}

const Line = styled.div`
  grid-area: line;
  border-top: solid 1px ${theme.color.border.border01};
  margin-right: ${theme.spacing.spacing03};
  height: ${theme.spacing.spacing02};
`;
